import type { ReactElement, ReactNode } from 'react';

import type { AdminMaintenance } from '@/admin/adminApi';
import type { SensitiveActionConsequence } from '@/admin/SensitiveActionDialog';
import type { SensitiveActionKind } from '@/admin/sensitiveActions';
import { formatInstant } from '@/admin/users/accountPresentation';

import {
  MAINTENANCE_ENABLED_KEY,
  MAINTENANCE_ENDS_AT_KEY,
  MAINTENANCE_STARTS_AT_KEY,
  windowPhaseOf,
} from './maintenanceModel';

/**
 * Ce que l'ecran dit de la maintenance, isole du rendu.
 *
 * Le texte est ici pour une raison : **c'est la seule chose qui separe un
 * administrateur d'une coupure qu'il n'avait pas prevue**. Il doit pouvoir etre
 * relu, teste et corrige sans toucher a la mecanique de l'ecran. Les faits qu'il
 * enonce sont ceux du serveur (`MaintenanceModeSubscriber`) : 503 avec un delai de
 * nouvel essai de cinq minutes, trois exemptions — la sonde de sante,
 * l'authentification et l'administration —, et rien de touche aux donnees.
 */

/** Avertissement permanent : le mobile ne montre pas encore le message (MOB-26, #523). */
export const MobileLimitNotice = (): ReactElement => (
  <>
    <strong>L’application mobile n’affiche pas encore ce message.</strong> Tant que MOB-26 (issue 523) n’est pas livrée, un
    parent verra « Impossible de joindre le serveur pour le moment… », comme pour une panne réseau : ses saisies
    restent sur le téléphone et partiront ensuite, mais rien n’y dit que la coupure est voulue. Programmer ou activer
    une maintenance avant cela est une décision d’exploitation consciente, à annoncer autrement que par l’application.
  </>
);

/** Ce qui reste servi quand l'API est suspendue. */
export const MAINTENANCE_EXEMPTIONS =
  'La connexion, l’administration et la sonde de santé restent servies : sans elles, personne ne pourrait se reconnecter pour rétablir le service.';

/** Phrase d'etat sous le badge, d'apres l'etat calcule par le serveur. */
export const describeServerState = (state: AdminMaintenance): string => {
  switch (windowPhaseOf(state)) {
    case 'running':
      return `Une fenêtre est en cours : le service redevient disponible le ${formatInstant(state.nextChangeAt)}, sans écriture.`;
    case 'upcoming':
      return `Une fenêtre est programmée : le service sera suspendu le ${formatInstant(state.nextChangeAt)}, sans écriture.`;
    case 'ended':
      return 'La fenêtre programmée est terminée : le service est revenu de lui-même, sans écriture.';
    case 'overridden':
      return 'Le commutateur est allumé : la fenêtre programmée ne change plus rien tant qu’il l’est.';
    case 'none':
      return state.enabled
        ? 'La maintenance a été activée à la main : elle dure jusqu’à ce que vous la désactiviez.'
        : 'Aucune maintenance en cours ni programmée : l’API répond normalement.';
  }
};

interface ActionCopy {
  readonly title: string;
  readonly actionLabel: string;
  readonly consequence: SensitiveActionConsequence;
}

const KEPT_DATA = 'Toutes les données, sans exception';

const instantOf = (values: Readonly<Record<string, boolean | string>>, key: string): string | null => {
  const value = values[key];

  return typeof value === 'string' && value !== '' ? value : null;
};

/**
 * Titre, bouton et consequence de la boite de confirmation, selon ce qui est
 * ecrit. Trois gestes se distinguent, parce que leurs consequences different :
 * couper tout de suite, programmer une coupure, et tout le reste (rien ne se
 * coupe : retablir, effacer la fenetre, changer le texte).
 */
export const describeAction = (
  values: Readonly<Record<string, boolean | string>>,
  kind: SensitiveActionKind,
  timeZone: string,
): ActionCopy => {
  const starts = instantOf(values, MAINTENANCE_STARTS_AT_KEY);
  const ends = instantOf(values, MAINTENANCE_ENDS_AT_KEY);

  if (values[MAINTENANCE_ENABLED_KEY] === true) {
    return {
      title: 'Activer la maintenance',
      actionLabel: 'Suspendre l’API',
      consequence: {
        summary: (
          <>
            <strong>L’API va être suspendue pour tous les clients</strong>, dès l’enregistrement.{' '}
            {MAINTENANCE_EXEMPTIONS}
          </>
        ),
        disappearsTitle: 'Est suspendu',
        disappears: [
          'Le service de l’API pour toute l’application mobile : chaque requête reçoit un 503',
          'Les synchronisations et les écritures depuis les téléphones',
        ],
        keptTitle: 'Reste servi',
        kept: [KEPT_DATA, 'La connexion, l’administration et la sonde de santé : vous pourrez rétablir le service'],
      },
    };
  }

  if (kind === 'enable_maintenance' && starts !== null && ends !== null) {
    const when = (iso: string): ReactNode => <strong>{formatInstant(iso)}</strong>;

    return {
      title: 'Programmer la maintenance',
      actionLabel: 'Programmer la maintenance',
      consequence: {
        summary: (
          <>
            L’API se suspendra d’elle-même le {when(starts)} et redeviendra disponible le {when(ends)} (heure : {timeZone}
            ). La bascule se fait <strong>sans geste et sans ligne d’audit</strong> : la ligne écrite maintenant fait
            foi. {MAINTENANCE_EXEMPTIONS}
          </>
        ),
        disappearsTitle: 'Sera suspendu pendant la fenêtre',
        disappears: ['Le service de l’API pour toute l’application mobile : chaque requête recevra un 503'],
        keptTitle: 'Reste servi',
        kept: [KEPT_DATA, 'L’API jusqu’au début de la fenêtre, et de nouveau après sa fin'],
      },
    };
  }

  return {
    title: 'Enregistrer la maintenance',
    actionLabel: values[MAINTENANCE_ENABLED_KEY] === false ? 'Rétablir le service' : 'Enregistrer',
    consequence: {
      summary:
        values[MAINTENANCE_ENABLED_KEY] === false ? (
          <>
            <strong>Le commutateur s’éteint</strong> : l’API redevient disponible dès l’enregistrement, sauf si une
            fenêtre programmée est en cours.
          </>
        ) : (
          'Le serveur appliquera ces valeurs dès l’enregistrement. Rien ne se coupe.'
        ),
      disappearsTitle: 'Est remplacé',
      disappears: ['Les valeurs actuelles des réglages modifiés'],
      kept: [KEPT_DATA, 'Les anciennes valeurs, inscrites au journal d’audit avec votre motif'],
    },
  };
};

/** Phrase apres une ecriture, lue sur l'etat que le serveur vient de rendre. */
export const confirmationMessageFor = (state: AdminMaintenance): string =>
  state.activeNow
    ? 'Enregistré. Le serveur indique que l’API répond 503 à tous les clients en ce moment.'
    : 'Enregistré. Le serveur indique que l’API répond normalement en ce moment.';
